import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ChangeRequestSourceBadge } from '@/components/change-requests/ChangeRequestSourceBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  useChangeRequest,
  useApproveChangeRequest,
  useRejectChangeRequest,
} from '@/hooks/react-query/useChangeRequests';
import { DataLoader } from '@/components/ui/DataLoader';
import { formatDate } from '@/utils/formatters';
import type { ChangeRequestDetail } from '@/types/changeRequest';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

interface ChangeRequestReviewDialogProps {
  requestId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ChangeRequestReviewDialog({
  requestId,
  isOpen,
  onClose,
}: ChangeRequestReviewDialogProps) {
  const { data: request, isLoading, error } = useChangeRequest(requestId);
  const approveMutation = useApproveChangeRequest();
  const rejectMutation = useRejectChangeRequest();

  const [reviewerNotes, setReviewerNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const handleApprove = async () => {
    try {
      await approveMutation.mutateAsync({
        requestId,
        reviewerNotes: reviewerNotes.trim() || undefined,
      });
      onClose();
      setReviewerNotes('');
    } catch (error) {
      console.error('Failed to approve request:', error);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      return;
    }

    try {
      await rejectMutation.mutateAsync({
        requestId,
        rejectionReason,
        reviewerNotes: reviewerNotes.trim() || undefined,
      });
      onClose();
      setRejectionReason('');
      setReviewerNotes('');
      setShowRejectForm(false);
    } catch (error) {
      console.error('Failed to reject request:', error);
    }
  };

  const FIELD_LABEL_OVERRIDES: Record<string, string> = {
    iban: 'IBAN',
    bank_swift_code: 'Bank SWIFT/BIC Code',
  };

  const formatFieldName = (field: string): string => {
    if (FIELD_LABEL_OVERRIDES[field]) return FIELD_LABEL_OVERRIDES[field];
    return field
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Change Data</DialogTitle>
          <DialogDescription>
            Review and approve or reject this change data
          </DialogDescription>
        </DialogHeader>

        <DataLoader<ChangeRequestDetail>
          isLoading={isLoading}
          error={error}
          data={request}
        >
          {(request) => (
            <div className="space-y-6">
              {/* Request Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Investor:
                  </span>
                  <p className="text-sm font-semibold">
                    {request.investor_name}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Request Type:
                  </span>
                  <div className="mt-1">
                    <Badge>{formatFieldName(request.request_type)}</Badge>
                  </div>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Requested By:
                  </span>
                  <p className="text-sm">{request.requested_by_contact_name}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Source:
                  </span>
                  <div className="mt-1">
                    <ChangeRequestSourceBadge source={request.source} />
                  </div>
                  {request.source === 'geena' && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Changed by the investor in their Geena. Approving applies
                      it here; nothing is sent back.
                    </p>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Requested At:
                  </span>
                  <p className="text-sm">{formatDate(request.requested_at)}</p>
                </div>
              </div>

              {/* Reason */}
              {request.change_reason && (
                <div>
                  <span className="text-sm font-medium">
                    Reason for Change:
                  </span>
                  <Alert className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{request.change_reason}</AlertDescription>
                  </Alert>
                </div>
              )}

              {/* Notes */}
              {request.notes && (
                <div>
                  <span className="text-sm font-medium">Additional Notes:</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    {request.notes}
                  </p>
                </div>
              )}

              {/* Changes */}
              <div>
                <span className="text-sm font-medium">Proposed Changes:</span>
                <div className="mt-2 space-y-3 bg-muted/50 p-4 rounded-lg">
                  {Object.entries(request.new_values).map(
                    ([field, newValue]) => (
                      <div
                        key={field}
                        className="grid grid-cols-[200px_1fr_1fr] gap-4 text-sm border-b pb-3 last:border-0"
                      >
                        <span className="font-medium">
                          {formatFieldName(field)}:
                        </span>
                        <div>
                          <span className="text-xs text-muted-foreground block mb-1">
                            Current
                          </span>
                          <span className="line-through text-muted-foreground">
                            {request.old_values[field]
                              ? String(request.old_values[field])
                              : '(empty)'}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground block mb-1">
                            Proposed
                          </span>
                          <span className="text-green-600 font-medium">
                            {newValue ? String(newValue) : '(empty)'}
                          </span>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Review Form */}
              {!showRejectForm ? (
                <div className="space-y-2">
                  <Label htmlFor="reviewer-notes">
                    Reviewer Notes (Optional)
                  </Label>
                  <Textarea
                    id="reviewer-notes"
                    value={reviewerNotes}
                    onChange={(e) => setReviewerNotes(e.target.value)}
                    placeholder="Add any notes about this review..."
                    rows={3}
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="rejection-reason">
                      Rejection Reason <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="rejection-reason"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Explain why this request is being rejected..."
                      rows={3}
                      className="border-red-200 focus:border-red-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reviewer-notes-reject">
                      Additional Notes (Optional)
                    </Label>
                    <Textarea
                      id="reviewer-notes-reject"
                      value={reviewerNotes}
                      onChange={(e) => setReviewerNotes(e.target.value)}
                      placeholder="Add any additional notes..."
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </DataLoader>

        <DialogFooter>
          {!showRejectForm ? (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowRejectForm(true)}
                disabled={approveMutation.isPending}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending ? (
                  'Approving...'
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Approve & Apply Changes
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectionReason('');
                }}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={rejectMutation.isPending || !rejectionReason.trim()}
              >
                {rejectMutation.isPending
                  ? 'Rejecting...'
                  : 'Confirm Rejection'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
